// Importing the `cn` utility function for class name concatenation
import { cn } from '../../lib/utils';

// Define the interface for the `FormField` component's props
interface FormFieldProps {
  label: string; // The label displayed above the input/select field
  name: string; // The name and id attribute for the input/select field
  type?: string; // The input type (e.g., text, email, password). Default is 'text'.
  value: string; // The current value of the input/select field
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void; // Callback for value changes
  required?: boolean; // Indicates if the field is required. Default is `false`.
  options?: Array<{ value: string; label: string }>; // For rendering a dropdown (select) field
  error?: string; // Error message to display if validation fails
  className?: string; // Additional class names for custom styling
}

// Functional component for rendering a form field
export function FormField({
  label,       // Label text for the field
  name,        // Name and ID for the field
  type = 'text', // Default type is 'text' unless specified
  value,       // Current value of the field
  onChange,    // Callback to handle value changes
  required = false, // Whether the field is required
  options,     // Options for a dropdown (select) field
  error,       // Error message for validation
  className,   // Custom class names
}: FormFieldProps) {
  // Dynamic class names for the input or select element
  const inputClasses = cn(
    "mt-1 block w-full rounded-lg", // General styling for inputs
    "border-gray-300 shadow-sm",   // Light border and shadow for default state
    "focus:border-blue-500 focus:ring-blue-500", // Blue focus styling
    "transition duration-200",     // Smooth transitions for hover/focus
    error && "border-red-300 text-red-900 placeholder-red-300", // Styling for error state
    "disabled:bg-gray-50 disabled:text-gray-500" // Styling for disabled state
  );

  return (
    <div className={className}>
      {/* Label for the input/select field */}
      <label htmlFor={name} className="block text-sm font-medium text-gray-700">
        {label}
        {/* Display a red asterisk for required fields */}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {/* Conditionally render a select dropdown or an input field */}
      {options ? (
        // Render a select element if options are provided
        <select
          id={name}
          name={name}
          value={value}
          onChange={onChange}
          required={required}
          className={inputClasses}
        >
          <option value="">Sélectionnez...</option>
          {/* Map through options to render dropdown choices */}
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        // Render an input element if no options are provided
        <input
          type={type} // Input type (e.g., text, email)
          id={name} // ID and name attribute
          name={name} // Name attribute
          value={value} // Current value
          onChange={onChange} // OnChange callback
          required={required} // Mark as required if specified
          className={inputClasses} // Dynamic classes
        />
      )}

      {/* Render an error message if the error prop is provided */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}
